import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { ChevronRight, ChevronDown, Download, Maximize2 } from 'lucide-react';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Node {
  id: string;
  label: string;
  level: number;
  children: Node[];
  content: string;
}

interface ParsedMarkdown {
  nodes: Node[];
  flatNodes: Node[];
}

interface LayoutNode {
  node: Node;
  x: number;
  y: number;
  width: number;
  height: number;
  depth: number;
}

interface Connector {
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
}

// ── Layout constants ──────────────────────────────────────────────────────────

const NODE_W = 160;
const H_GAP = 60;
const V_GAP = 12;
const PAD = 40;
const NODE_VPAD = 20;       // total top+bottom padding inside node (10px each side)
const LINE_H = 16;          // px per text line
const CHARS_PER_LINE = 18;  // approx chars per line at 11px font in NODE_W - NODE_HPAD

// ── Pure layout helpers (module-level, no component dependency) ───────────────

function wrapText(label: string, charsPerLine: number): string[] {
  const words = label.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let current = '';

  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (candidate.length > charsPerLine && current) {
      lines.push(current);
      current = word;
    } else {
      current = candidate;
    }
  }
  if (current) lines.push(current);
  return lines;
}

function nodeHeight(label: string): number {
  return wrapText(label, CHARS_PER_LINE).length * LINE_H + NODE_VPAD;
}

function subtreeHeight(node: Node, expanded: Set<string>): number {
  const h = nodeHeight(node.label);
  if (!expanded.has(node.id) || node.children.length === 0) return h;
  const childrenTotal = node.children.reduce(
    (sum, child) => sum + subtreeHeight(child, expanded),
    0
  );
  const childrenH = childrenTotal + (node.children.length - 1) * V_GAP;
  return Math.max(h, childrenH);
}

function computeLayout(
  roots: Node[],
  expanded: Set<string>
): { layoutNodes: LayoutNode[]; connectors: Connector[] } {
  const layoutNodes: LayoutNode[] = [];
  const connectors: Connector[] = [];

  function place(node: Node, depth: number, startY: number) {
    const h = nodeHeight(node.label);
    const totalH = subtreeHeight(node, expanded);
    const centerY = startY + totalH / 2;
    const x = PAD + depth * (NODE_W + H_GAP);
    const y = centerY - h / 2;

    layoutNodes.push({ node, x, y, width: NODE_W, height: h, depth });

    if (expanded.has(node.id) && node.children.length > 0) {
      let childStartY = startY;
      node.children.forEach(child => {
        const childH = subtreeHeight(child, expanded);
        const childCenterY = childStartY + childH / 2;
        const childX = PAD + (depth + 1) * (NODE_W + H_GAP);

        connectors.push({
          fromX: x + NODE_W,
          fromY: centerY,
          toX: childX,
          toY: childCenterY,
        });

        place(child, depth + 1, childStartY);
        childStartY += childH + V_GAP;
      });
    }
  }

  let currentY = PAD;
  roots.forEach((root, i) => {
    place(root, 0, currentY);
    currentY += subtreeHeight(root, expanded) + (i < roots.length - 1 ? V_GAP * 4 : 0);
  });

  return { layoutNodes, connectors };
}

function getAllIds(nodes: Node[]): string[] {
  const ids: string[] = [];
  function traverse(node: Node) {
    ids.push(node.id);
    node.children.forEach(traverse);
  }
  nodes.forEach(traverse);
  return ids;
}

function diagramBounds(nodes: LayoutNode[]): { width: number; height: number } {
  if (nodes.length === 0) return { width: 400, height: 300 };
  return {
    width: Math.max(...nodes.map(n => n.x + n.width)) + PAD,
    height: Math.max(...nodes.map(n => n.y + n.height)) + PAD,
  };
}

// ── Component ─────────────────────────────────────────────────────────────────

const MindmapView: React.FC<{ parsedData: ParsedMarkdown }> = ({ parsedData }) => {
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [transform, setTransform] = useState({ x: PAD, y: PAD, scale: 1 });
  const [initialTransform, setInitialTransform] = useState({ x: PAD, y: PAD, scale: 1 });
  const [isDragging, setIsDragging] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const transformRef = useRef(transform);
  const dragStartRef = useRef({ mouseX: 0, mouseY: 0, tx: 0, ty: 0 });

  // Keep transformRef in sync so stable handlers always read the latest value
  useEffect(() => { transformRef.current = transform; }, [transform]);

  // ── Layout (memoized) ──────────────────────────────────────────────────────

  const { layoutNodes, connectors } = useMemo(
    () => computeLayout(parsedData.nodes, expandedNodes),
    [parsedData.nodes, expandedNodes]
  );

  const { width: diagW, height: diagH } = useMemo(
    () => diagramBounds(layoutNodes),
    [layoutNodes]
  );

  // ── Fit to screen on new data ──────────────────────────────────────────────

  useEffect(() => {
    setExpandedNodes(new Set());

    // Delay to let the container measure after mount/re-mount
    const timer = setTimeout(() => {
      const container = containerRef.current;
      if (!container || parsedData.nodes.length === 0) return;

      // Base the initial fit on the roots-only layout (all collapsed)
      const { layoutNodes: rootLayout } = computeLayout(parsedData.nodes, new Set());
      const { width: w, height: h } = diagramBounds(rootLayout);

      const cw = container.clientWidth || 800;
      const ch = container.clientHeight || 600;
      const scale = Math.min((cw - PAD * 2) / w, (ch - PAD * 2) / h, 1.5);
      const tx = (cw - w * scale) / 2;
      const ty = (ch - h * scale) / 2;
      const t = { x: tx, y: ty, scale };
      setTransform(t);
      setInitialTransform(t);
    }, 50);

    return () => clearTimeout(timer);
  }, [parsedData]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Non-passive wheel listener (required to call preventDefault) ───────────

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const factor = e.deltaY < 0 ? 1.1 : 0.9;
      const rect = container.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      setTransform(t => {
        const newScale = Math.min(3, Math.max(0.2, t.scale * factor));
        const ratio = newScale / t.scale;
        return {
          scale: newScale,
          x: mx - (mx - t.x) * ratio,
          y: my - (my - t.y) * ratio,
        };
      });
    };

    container.addEventListener('wheel', onWheel, { passive: false });
    return () => container.removeEventListener('wheel', onWheel);
  }, []);

  // ── Pan handlers ───────────────────────────────────────────────────────────

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if ((e.target as Element).closest('[data-node]')) return;
    e.preventDefault();
    setIsDragging(true);
    dragStartRef.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      tx: transformRef.current.x,
      ty: transformRef.current.y,
    };
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging) return;
    const { mouseX, mouseY, tx, ty } = dragStartRef.current;
    setTransform(t => ({
      ...t,
      x: tx + (e.clientX - mouseX),
      y: ty + (e.clientY - mouseY),
    }));
  }, [isDragging]);

  const handleMouseUp = useCallback(() => setIsDragging(false), []);

  // ── Collapse / Expand ──────────────────────────────────────────────────────

  const toggleNode = useCallback((nodeId: string) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        const collapseSubtree = (node: Node) => {
          next.delete(node.id);
          node.children.forEach(collapseSubtree);
        };
        const target = parsedData.flatNodes.find(n => n.id === nodeId);
        if (target) collapseSubtree(target);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  }, [parsedData.flatNodes]);

  const expandAll = useCallback(() => {
    setExpandedNodes(new Set(getAllIds(parsedData.nodes)));
  }, [parsedData.nodes]);

  const collapseAll = useCallback(() => setExpandedNodes(new Set()), []);

  const resetView = useCallback(() => setTransform(initialTransform), [initialTransform]);

  // ── PNG Export ─────────────────────────────────────────────────────────────

  const exportPNG = useCallback(() => {
    const svgEl = svgRef.current;
    if (!svgEl || layoutNodes.length === 0) return;

    const exportScale = 2;
    const cloned = svgEl.cloneNode(true) as SVGSVGElement;
    cloned.setAttribute('width', String(diagW * exportScale));
    cloned.setAttribute('height', String(diagH * exportScale));
    cloned.setAttribute('viewBox', `0 0 ${diagW} ${diagH}`);

    // Remove the pan/zoom transform so the full diagram is visible in the export
    const group = cloned.querySelector('[data-transform-group]');
    if (group) group.setAttribute('transform', '');

    const svgStr = '<?xml version="1.0" encoding="UTF-8"?>'
      + new XMLSerializer().serializeToString(cloned);
    const url = URL.createObjectURL(
      new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' })
    );

    const canvas = document.createElement('canvas');
    canvas.width = diagW * exportScale;
    canvas.height = diagH * exportScale;
    const ctx = canvas.getContext('2d');
    if (!ctx) { URL.revokeObjectURL(url); return; }

    const img = new Image();
    img.onload = () => {
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      const link = document.createElement('a');
      link.download = 'mindmap-export.png';
      link.href = canvas.toDataURL('image/png');
      link.click();
      URL.revokeObjectURL(url);
    };
    img.onerror = () => URL.revokeObjectURL(url);
    img.src = url;
  }, [layoutNodes, diagW, diagH]);

  // ── Node appearance helpers ────────────────────────────────────────────────

  // 4-tier depth progression: dark root → lighter sub-root → sky parent → pale leaf
  const getNodeFill = (depth: number, hasChildren: boolean): string => {
    if (!hasChildren) return '#e0f2fe';   // leaf:         sky-100 pale tint
    if (depth === 0)  return '#1e3a8a';   // root #:       blue-900 deep navy
    if (depth === 1)  return '#2563eb';   // sub-root ##:  blue-600
    return '#0284c7';                     // inner parent: sky-600 (dark enough for white text)
  };

  const getNodeStroke = (_depth: number, hasChildren: boolean): string =>
    !hasChildren ? '#bae6fd' : 'none';    // sky-200 border on leaves only

  const getTextFill = (_depth: number, hasChildren: boolean): string => {
    if (!hasChildren) return '#0c4a6e';   // leaf: sky-900 dark on pale bg
    return 'white';                       // all parent tiers: white on dark/medium bg
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full" style={{ minHeight: '75vh' }}>

      {/* Header */}
      <div className="mb-4">
        <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
          Mindmap
        </h2>
        <p className="text-gray-600 mt-1">
          Click nodes to expand · Drag to pan · Scroll to zoom
        </p>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-center space-x-3 mb-4">
        <button
          onClick={expandedNodes.size > 0 ? collapseAll : expandAll}
          className={`flex items-center justify-center space-x-2 text-white px-4 py-2 h-10 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg font-medium min-w-[120px] ${
            expandedNodes.size > 0
              ? 'bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800'
              : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800'
          }`}
        >
          {expandedNodes.size > 0 ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
          <span>{expandedNodes.size > 0 ? 'Collapse All' : 'Expand All'}</span>
        </button>
        <button
          onClick={resetView}
          className="flex items-center justify-center space-x-2 bg-gradient-to-r from-slate-500 to-slate-600 text-white px-4 py-2 h-10 rounded-lg hover:from-slate-600 hover:to-slate-700 transition-all duration-200 shadow-md hover:shadow-lg font-medium min-w-[120px]"
        >
          <Maximize2 size={16} />
          <span>Reset View</span>
        </button>
        <button
          onClick={exportPNG}
          className="flex items-center justify-center space-x-2 bg-gradient-to-r from-green-600 to-green-700 text-white px-4 py-2 h-10 rounded-lg hover:from-green-700 hover:to-green-800 transition-all duration-200 shadow-md hover:shadow-lg font-medium min-w-[120px]"
        >
          <Download size={16} />
          <span>Export PNG</span>
        </button>
      </div>

      {/* Canvas */}
      <div
        ref={containerRef}
        className="border border-gray-200 rounded-xl overflow-hidden bg-gray-50"
        style={{ height: '65vh', cursor: isDragging ? 'grabbing' : 'grab' }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <svg
          ref={svgRef}
          width="100%"
          height="100%"
          style={{ display: 'block' }}
        >
          {/* White background for PNG export */}
          <rect width="100%" height="100%" fill="white" />

          <g
            data-transform-group="true"
            transform={`translate(${transform.x},${transform.y}) scale(${transform.scale})`}
          >
            {/* Connectors drawn first so nodes render on top */}
            {connectors.map((c, i) => {
              const mx = (c.toX - c.fromX) / 2;
              return (
                <path
                  key={`c-${i}`}
                  d={`M${c.fromX},${c.fromY} C${c.fromX + mx},${c.fromY} ${c.toX - mx},${c.toY} ${c.toX},${c.toY}`}
                  stroke="#94a3b8"
                  strokeWidth={1.5}
                  fill="none"
                  pointerEvents="none"
                />
              );
            })}

            {/* Nodes */}
            {layoutNodes.map(({ node, x, y, width, height, depth }) => {
              const hasChildren = node.children.length > 0;
              const isCollapsed = hasChildren && !expandedNodes.has(node.id);

              return (
                <g
                  key={node.id}
                  data-node={node.id}
                  transform={`translate(${x},${y})`}
                  onClick={() => hasChildren && toggleNode(node.id)}
                  style={{ cursor: hasChildren ? 'pointer' : 'default' }}
                >
                  {/* Node rectangle */}
                  <rect
                    width={width}
                    height={height}
                    rx={6}
                    fill={getNodeFill(depth, hasChildren)}
                    stroke={getNodeStroke(depth, hasChildren)}
                    strokeWidth={1.5}
                  />

                  {/* Label — multi-line tspan, node rect sized to fit */}
                  {(() => {
                    const textX = isCollapsed ? NODE_W / 2 - 10 : NODE_W / 2;
                    const lines = wrapText(node.label, CHARS_PER_LINE);
                    const firstY = height / 2 - ((lines.length - 1) * LINE_H) / 2;
                    return (
                      <text
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fontSize={11}
                        fontFamily="Arial, Helvetica, sans-serif"
                        fill={getTextFill(depth, hasChildren)}
                        style={{ userSelect: 'text', cursor: 'text' }}
                      >
                        {lines.map((line, i) => (
                          <tspan key={i} x={textX} y={firstY + i * LINE_H}>
                            {i < lines.length - 1 ? `${line} ` : line}
                          </tspan>
                        ))}
                      </text>
                    );
                  })()}

                  {/* Collapsed badge (+) */}
                  {isCollapsed && (
                    <g transform={`translate(${width - 18},${height / 2 - 8})`}>
                      <circle cx={8} cy={8} r={8} fill="rgba(0,0,0,0.15)" />
                      <text
                        x={8}
                        y={8}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fontSize={11}
                        fontFamily="Arial, Helvetica, sans-serif"
                        fontWeight="bold"
                        fill={getTextFill(depth, hasChildren)}
                        style={{ userSelect: 'none', pointerEvents: 'none' }}
                      >
                        +
                      </text>
                    </g>
                  )}
                </g>
              );
            })}
          </g>
        </svg>
      </div>
    </div>
  );
};

export default MindmapView;
